import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { StatusTag } from "@/views/components/StatusTag";
import { Button, Card, Input, Space, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { ProductTypeIndexClick, ProductTypeTopVo } from "@zhihaoscm/entity";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Dialog as ZhDialog, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DATETIME_PICKER_FORMAT, DEFAULT_VALUE } from "@/config";
import { dateFormat } from "@/utils";
import { formatMoney } from "@/utils/money";
import { PRODUCT_TYPE_INDEX } from "@/meta/supply/productType";
import { CLIENT_TYPE, PIN_TO_TP } from "@zhihaoscm/meta";
import { useUserStore } from "@/views/stores";

export interface IndexResearchRow {
    virtualId?: string;
    hasChildren?: boolean;
    children?: IndexResearchRow[];
    marketName?: string;
    lastTimeUpDown?: number;
    productType?: { id?: string | number; name?: string; area?: string; topCount?: number | string };
    productTypeIndex?: {
        id?: number;
        avgPrice?: number;
        clickCount?: number | string;
        date?: string;
        placePriceType?: string;
        priceType?: number;
        shipment?: number;
        state?: number;
        updatedTime?: string;
    };
    topCount?: number | string;
    productTypeIndexVersion?: { id?: number; versionDate?: string };
}

export interface IndexResearchQuery {
    page: number;
    size: number;
    keyword?: string;
    startTime?: string;
    endTime?: string;
    sortKey?: string;
    sortOrder?: "asc" | "desc";
}

export interface IndexResearchPageResult {
    list?: IndexResearchRow[];
    total?: number;
}

export interface IndexResearchChildQuery {
    date: string;
    id: string | number;
}

export interface IndexResearchPageProps {
    loadIndex?: (query: IndexResearchQuery) => Promise<IndexResearchPageResult>;
    loadChildDetails?: (query: IndexResearchChildQuery) => Promise<IndexResearchRow[]>;
    loadClickDetails?: (query: IndexResearchClickQuery) => Promise<IndexResearchClickPageResult>;
    loadTopDetails?: (query: IndexResearchTopQuery) => Promise<IndexResearchTopPageResult>;
    publishIndex?: (row: IndexResearchRow) => Promise<void>;
    recallIndex?: (row: IndexResearchRow) => Promise<void>;
    deleteIndex?: (row: IndexResearchRow) => Promise<void>;
    permissions?: string[];
}

export interface IndexResearchClickQuery {
    page: number;
    size: number;
    productTypeIndexId: number;
}

export interface IndexResearchClickPageResult {
    list?: ProductTypeIndexClick[];
    total?: number;
}

export interface IndexResearchTopQuery {
    page: number;
    size: number;
    productTypeId: string | number;
}

export interface IndexResearchTopPageResult {
    list?: ProductTypeTopVo[];
    total?: number;
}

const defaultQuery: IndexResearchQuery = { page: 1, size: 10 };

/** 复用 Vue 砂石指数列表接口，保留原列表分页和筛选参数。 */
export async function loadIndexResearch(query: IndexResearchQuery): Promise<IndexResearchPageResult> {
    // 复用 Vue indexService.paging 的真实接口路径，避免改动共享服务的历史泛型定义。
    const response = await request.service.get<IndexResearchPageResult>(
        "/dataService/admin/product-type-index/paging",
        { params: query }
    );
    return response.data;
}

/** 复用 Vue 指数子行接口，保持懒加载时的日期和品类编号契约。 */
export async function loadIndexResearchChildDetails(query: IndexResearchChildQuery): Promise<IndexResearchRow[]> {
    const response = await request.service.get<IndexResearchRow[]>(
        `/dataService/admin/product-type-index/date/${query.date}/product-type-id/${query.id}`
    );
    return response.data;
}

/** 复用 Vue 点击明细分页接口，保留 productTypeIndexId 和分页参数契约。 */
export async function loadIndexResearchClickDetails(
    query: IndexResearchClickQuery
): Promise<IndexResearchClickPageResult> {
    const { productTypeIndex } = await import("@/api/services/productTypeIndexClick");
    return productTypeIndex.productTypeIndexClickPaging({
        page: String(query.page),
        size: String(query.size),
        productTypeIndexId: query.productTypeIndexId
    });
}

/** 复用 Vue 置顶明细分页接口，保留 productTypeId 和分页参数契约。 */
export async function loadIndexResearchTopDetails(query: IndexResearchTopQuery): Promise<IndexResearchTopPageResult> {
    // 直接复用旧 Vue indexService.topDetail 的路径，避免引入共享服务中与基类不一致的历史泛型覆写。
    const response = await request.service.get<IndexResearchTopPageResult>(
        "/dataService/admin/product-type-index/top-detail",
        {
            params: {
                page: String(query.page),
                size: String(query.size),
                productTypeId: String(query.productTypeId)
            }
        }
    );
    return response.data;
}

/** 沿用 Vue indexService.updateState 的发布接口。 */
export async function publishIndex(row: IndexResearchRow): Promise<void> {
    await updateIndexState(row, 1);
}

/** 沿用 Vue indexService.updateState 的撤回接口。 */
export async function recallIndex(row: IndexResearchRow): Promise<void> {
    await updateIndexState(row, 0);
}

/** 沿用 Vue indexService.deleteByDate 的删除接口。 */
export async function deleteIndex(row: IndexResearchRow): Promise<void> {
    const path = getIndexActionPath(row);
    if (!path) return;
    await request.service.delete(path);
}

/** 构造旧 Vue 指数状态接口和删除接口共用的路径。 */
function getIndexActionPath(row: IndexResearchRow): string | null {
    const date = row.productTypeIndex?.date;
    const productTypeId = row.productType?.id;
    if (!date || productTypeId == null) return null;
    return `/dataService/admin/product-type-index/date/${date}/product-type-id/${productTypeId}`;
}

/** 按旧 Vue 状态值提交发布或撤回请求。 */
async function updateIndexState(row: IndexResearchRow, state: number): Promise<void> {
    const path = getIndexActionPath(row);
    if (!path) return;
    await request.service.put(`${path}/state/${state}`);
}

/** 指数管理最小可用列表：首屏加载、关键词/版本日期筛选及异常反馈。 */
export function IndexResearchPage({
    loadIndex = loadIndexResearch,
    loadChildDetails = loadIndexResearchChildDetails,
    loadClickDetails = loadIndexResearchClickDetails,
    loadTopDetails = loadIndexResearchTopDetails,
    publishIndex: publish = publishIndex,
    recallIndex: recall = recallIndex,
    deleteIndex: remove = deleteIndex,
    permissions
}: IndexResearchPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const currentPermissions = permissions || storePermissions;
    const canManage = currentPermissions.includes("ROLE_CATEGORY_INDEX_MANAGE");
    const canDeal = currentPermissions.includes("ROLE_CATEGORY_INDEX_DEAL");
    const [rows, setRows] = useState<IndexResearchRow[]>([]);
    const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState<IndexResearchQuery>(defaultQuery);
    const [keyword, setKeyword] = useState("");
    const [startTime, setStartTime] = useState("");
    const [endTime, setEndTime] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [clickDetailRow, setClickDetailRow] = useState<IndexResearchRow | null>(null);
    const [clickDetailRows, setClickDetailRows] = useState<ProductTypeIndexClick[]>([]);
    const [clickDetailTotal, setClickDetailTotal] = useState(0);
    const [clickDetailPage, setClickDetailPage] = useState(1);
    const [clickDetailLoading, setClickDetailLoading] = useState(false);
    const [clickDetailError, setClickDetailError] = useState<string | null>(null);
    const [topDetailRow, setTopDetailRow] = useState<IndexResearchRow | null>(null);
    const [topDetailRows, setTopDetailRows] = useState<ProductTypeTopVo[]>([]);
    const [topDetailTotal, setTopDetailTotal] = useState(0);
    const [topDetailPage, setTopDetailPage] = useState(1);
    const [topDetailLoading, setTopDetailLoading] = useState(false);
    const [topDetailError, setTopDetailError] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        setLoading(true);
        void loadIndex(query)
            .then(result => {
                if (disposed) return;
                // 保留接口返回的主行对象，避免状态操作回调收到被迁移逻辑改写的行数据。
                setRows(result.list || []);
                setTotal(result.total || 0);
                setErrorMessage(null);
            })
            .catch(() => {
                if (disposed) return;
                setRows([]);
                setTotal(0);
                setErrorMessage("指数列表加载失败");
            })
            .finally(() => {
                if (!disposed) setLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [loadIndex, query]);

    const clickDetailId = clickDetailRow?.productTypeIndex?.id;

    useEffect(() => {
        if (clickDetailId == null) return;
        let disposed = false;
        setClickDetailLoading(true);
        void loadClickDetails({ page: clickDetailPage, size: 10, productTypeIndexId: clickDetailId })
            .then(result => {
                if (disposed) return;
                setClickDetailRows(result.list || []);
                setClickDetailTotal(result.total || 0);
                setClickDetailError(null);
            })
            .catch(() => {
                if (disposed) return;
                setClickDetailRows([]);
                setClickDetailTotal(0);
                setClickDetailError("点击明细加载失败");
            })
            .finally(() => {
                if (!disposed) setClickDetailLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [clickDetailId, clickDetailPage, loadClickDetails]);

    const topDetailProductTypeId = topDetailRow?.productType?.id;

    useEffect(() => {
        if (topDetailProductTypeId == null) return;
        let disposed = false;
        setTopDetailLoading(true);
        void loadTopDetails({ page: topDetailPage, size: 10, productTypeId: topDetailProductTypeId })
            .then(result => {
                if (disposed) return;
                setTopDetailRows(result.list || []);
                setTopDetailTotal(result.total || 0);
                setTopDetailError(null);
            })
            .catch(() => {
                if (disposed) return;
                setTopDetailRows([]);
                setTopDetailTotal(0);
                setTopDetailError("置顶明细加载失败");
            })
            .finally(() => {
                if (!disposed) setTopDetailLoading(false);
            });
        return () => {
            disposed = true;
        };
    }, [loadTopDetails, topDetailPage, topDetailProductTypeId]);

    const submitFilters = () => {
        const nextQuery: IndexResearchQuery = { ...defaultQuery };
        const normalizedKeyword = keyword.trim();
        if (normalizedKeyword) nextQuery.keyword = normalizedKeyword;
        if (startTime) nextQuery.startTime = startTime;
        if (endTime) nextQuery.endTime = endTime;
        setQuery(nextQuery);
    };

    const resetFilters = () => {
        setKeyword("");
        setStartTime("");
        setEndTime("");
        setQuery(defaultQuery);
    };

    /** 将 zhihao-ui-react 的排序事件转换为旧 Vue 指数接口参数，并重置到第一页。 */
    const handleSortChange = ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
        setQuery(currentQuery => {
            const nextQuery = { ...currentQuery, page: 1 };
            delete nextQuery.sortKey;
            delete nextQuery.sortOrder;
            if (prop === "productTypeIndex.avgPrice" && order) {
                nextQuery.sortKey = "avgPrice";
                nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
            }
            return nextQuery;
        });
    };

    /** 执行状态操作后刷新当前分页，失败时保留列表并提示用户。 */
    const runIndexAction = async (
        row: IndexResearchRow,
        action: (target: IndexResearchRow) => Promise<void>,
        failureMessage: string
    ) => {
        try {
            await action(row);
            const result = await loadIndex(query);
            setRows(result.list || []);
            setTotal(result.total || 0);
            setErrorMessage(null);
        } catch {
            setErrorMessage(failureMessage);
        }
    };

    /** 只有存在点击记录和指数编号时才打开 Vue 点击明细弹窗。 */
    const openClickDetails = (row: IndexResearchRow) => {
        if (Number(row.productTypeIndex?.clickCount || 0) <= 0 || row.productTypeIndex?.id == null) return;
        setClickDetailPage(1);
        setClickDetailRows([]);
        setClickDetailRow(row);
    };

    /** 只有存在置顶记录和品类编号时才打开 Vue 置顶明细弹窗。 */
    const openTopDetails = (row: IndexResearchRow) => {
        if (Number(getTopCount(row)) <= 0 || row.productType?.id == null) return;
        setTopDetailPage(1);
        setTopDetailRows([]);
        setTopDetailRow(row);
    };

    /** 旧 Vue 从 productType.topCount 读取置顶数，兼容历史分页结果的顶层字段。 */
    const getTopCount = (row: IndexResearchRow) => row.topCount ?? row.productType?.topCount ?? 0;

    /** 获取指数树行的稳定 key，供展开状态和表格行复用。 */
    const getIndexRowKey = (row: IndexResearchRow) => String(row.virtualId || row.productTypeIndex?.id || "index");

    /** 切换主指数展开状态，按旧 Vue 契约加载子指数并过滤重复主行。 */
    const handleIndexExpand = async (row: IndexResearchRow) => {
        const rowKey = getIndexRowKey(row);
        if (expandedRowKeys.includes(rowKey)) {
            // 旧 Vue 收起主行时会清空子行，下一次展开重新获取最新数据。
            setExpandedRowKeys(currentKeys => currentKeys.filter(key => key !== rowKey));
            setRows(currentRows =>
                currentRows.map(currentRow =>
                    getIndexRowKey(currentRow) === rowKey ? { ...currentRow, children: [] } : currentRow
                )
            );
            return;
        }

        const date = row.productTypeIndex?.date;
        const productTypeId = row.productType?.id;
        if (!date || productTypeId == null) {
            return;
        }

        try {
            const children = await loadChildDetails({ date, id: productTypeId });
            const nextChildren = children
                .filter(child => child.productTypeIndex?.id !== row.productTypeIndex?.id)
                .map(child => ({ ...child, hasChildren: false }));
            setRows(currentRows =>
                currentRows.map(currentRow =>
                    getIndexRowKey(currentRow) === rowKey ? { ...currentRow, children: nextChildren } : currentRow
                )
            );
            setExpandedRowKeys(currentKeys => [...currentKeys, rowKey]);
        } catch {
            setErrorMessage("指数子项加载失败");
        }
    };

    /** 仅在主行展开时把懒加载的子指数交给表格渲染。 */
    const visibleRows = rows.flatMap(row => [
        row,
        ...(expandedRowKeys.includes(getIndexRowKey(row)) ? row.children || [] : [])
    ]);

    // 用 zhihao-ui-react 列契约承接旧 Vue 指数列表字段和发布状态展示。
    const columns: TableColumnProps<IndexResearchRow>[] = [
        {
            key: "productTypeId",
            label: "品类代码",
            width: 100,
            prop: "virtualId",
            render: ({ row }) => row.productType?.id || "-"
        },
        {
            key: "productTypeName",
            label: "品类",
            minWidth: 92,
            prop: "virtualId",
            render: ({ row }) => row.productType?.name || "-"
        },
        {
            key: "area",
            label: "采区",
            minWidth: 204,
            prop: "virtualId",
            render: ({ row }) => row.productType?.area || "-"
        },
        {
            key: "priceName",
            label: "价格名称",
            width: 176,
            prop: "virtualId",
            render: ({ row }) => {
                const priceType = PRODUCT_TYPE_INDEX.getIntroByCode(row.productTypeIndex?.priceType);
                const priceName =
                    row.productTypeIndex?.priceType === PRODUCT_TYPE_INDEX.UP_BOAT.code
                        ? `${row.productTypeIndex.placePriceType || ""}${priceType}`
                        : `${row.marketName || ""}${priceType}`;
                return (
                    <Space>
                        {row.hasChildren !== false ? (
                            <Button
                                type="text"
                                size="small"
                                aria-label={`${expandedRowKeys.includes(getIndexRowKey(row)) ? "收起" : "展开"}指数 ${row.productType?.id || ""}`}
                                onClick={() => void handleIndexExpand(row)}
                            >
                                {expandedRowKeys.includes(getIndexRowKey(row)) ? "收起" : "展开"}
                            </Button>
                        ) : null}
                        {priceName || "-"}
                    </Space>
                );
            }
        },
        {
            key: "avgPrice",
            label: "成交均价(元/吨)",
            width: 148,
            prop: "productTypeIndex.avgPrice",
            sortable: "custom",
            render: ({ row }) => row.productTypeIndex?.avgPrice ?? "-"
        },
        {
            key: "lastTimeUpDown",
            label: "近一次涨跌",
            width: 120,
            prop: "virtualId",
            render: ({ row }) => {
                const amount = row.lastTimeUpDown;
                if (amount == null) return "-";
                return `${amount > 0 ? "+" : ""}${formatMoney(amount)}`;
            }
        },
        {
            key: "shipment",
            label: "出货量(吨)",
            width: 106,
            prop: "virtualId",
            render: ({ row }) => row.productTypeIndex?.shipment ?? "-"
        },
        {
            key: "versionDate",
            label: "版本日期",
            width: 138,
            prop: "virtualId",
            render: ({ row }) => {
                const versionId = row.productTypeIndexVersion?.id;
                const versionDate = row.productTypeIndexVersion?.versionDate || row.productTypeIndex?.date || "-";
                return versionId != null ? (
                    <Button
                        type="link"
                        aria-label={`查看版本详情 ${versionId}`}
                        onClick={() => navigate(`/indexResearch/versionInfo/${versionId}`)}
                    >
                        {versionDate}
                    </Button>
                ) : (
                    versionDate
                );
            }
        },
        {
            key: "updatedTime",
            label: "更新时间",
            width: 188,
            prop: "virtualId",
            render: ({ row }) =>
                row.productTypeIndex?.updatedTime
                    ? dateFormat(row.productTypeIndex.updatedTime, DATETIME_PICKER_FORMAT)
                    : "-"
        },
        {
            key: "clickCount",
            label: "点击量",
            prop: "virtualId",
            render: ({ row }) => {
                const clickCount = Number(row.productTypeIndex?.clickCount || 0);
                const indexId = row.productTypeIndex?.id;
                return clickCount > 0 && indexId != null ? (
                    <Button type="link" aria-label={`查看点击明细 ${indexId}`} onClick={() => openClickDetails(row)}>
                        {clickCount}
                    </Button>
                ) : null;
            }
        },
        {
            key: "topCount",
            label: "置顶数",
            prop: "virtualId",
            render: ({ row }) => {
                const topCount = Number(getTopCount(row));
                const productTypeId = row.productType?.id;
                return topCount > 0 && productTypeId != null ? (
                    <Button
                        type="link"
                        aria-label={`查看置顶明细 ${productTypeId}`}
                        onClick={() => openTopDetails(row)}
                    >
                        {topCount}
                    </Button>
                ) : null;
            }
        },
        {
            key: "state",
            label: "状态",
            prop: "virtualId",
            render: ({ row }) => <StatusTag>{row.productTypeIndex?.state === 1 ? "已发布" : "未发布"}</StatusTag>
        },
        {
            key: "operation",
            label: "操作",
            prop: "virtualId",
            render: ({ row }) => {
                if (row.hasChildren === false) return null;
                const productTypeId = row.productType?.id;
                const isPublished = row.productTypeIndex?.state === 1;
                return (
                    <Space>
                        {canManage ? (
                            <Button
                                type="link"
                                aria-label={`${isPublished ? "撤回" : "发布"}指数 ${productTypeId || ""}`}
                                onClick={() =>
                                    void runIndexAction(
                                        row,
                                        isPublished ? recall : publish,
                                        isPublished ? "指数撤回失败" : "指数发布失败"
                                    )
                                }
                            >
                                {isPublished ? "撤回" : "发布"}
                            </Button>
                        ) : null}
                        {canDeal && !isPublished ? (
                            <Button
                                type="link"
                                danger
                                aria-label={`删除指数 ${productTypeId || ""}`}
                                onClick={() => void runIndexAction(row, remove, "指数删除失败")}
                            >
                                删除
                            </Button>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    return (
        <section aria-label="index-research-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Typography.Title className="mb-4 mt-0" level={4}>
                    指数管理
                </Typography.Title>
                {errorMessage ? <Alert role="alert" message={errorMessage} type="error" /> : null}
                <form
                    className="mb-4"
                    onSubmit={event => {
                        event.preventDefault();
                        submitFilters();
                    }}
                >
                    <Space wrap>
                        <Input
                            aria-label="品类关键词"
                            placeholder="输入品类关键词或代码搜索"
                            value={keyword}
                            onChange={event => setKeyword(event.target.value)}
                        />
                        <Input
                            aria-label="版本开始日期"
                            placeholder="版本开始日期"
                            type="date"
                            value={startTime}
                            onChange={event => setStartTime(event.target.value)}
                        />
                        <Input
                            aria-label="版本结束日期"
                            placeholder="版本结束日期"
                            type="date"
                            value={endTime}
                            onChange={event => setEndTime(event.target.value)}
                        />
                        <Button htmlType="submit" type="primary">
                            查询指数
                        </Button>
                        <Button onClick={resetFilters}>重置</Button>
                    </Space>
                </form>
            </Card>
            <Card title="砂石指数">
                <ZhTable<IndexResearchRow>
                    data={visibleRows}
                    columns={normalizeOperationColumns(columns, visibleRows)}
                    pagination={false}
                    rowKey={getIndexRowKey}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            {loading ? <Typography.Text type="secondary">加载中</Typography.Text> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                hideOnSinglePage
                                onChange={page => setQuery(current => ({ ...current, page }))}
                            />
                        </div>
                    }
                />
            </Card>
            <ZhDialog
                open={clickDetailRow !== null}
                title="点击明细"
                width={800}
                onCancel={() => setClickDetailRow(null)}
                footer={null}
            >
                <ZhTable<ProductTypeIndexClick>
                    data={clickDetailRows}
                    columns={[
                        {
                            key: "customerCode",
                            label: "用户ID",
                            prop: "customerCode",
                            render: ({ row }) =>
                                row.customerId ? (
                                    <Link to={`/lianYunUser/userInfo?userId=${row.customerId}`}>
                                        {row.customerCode || DEFAULT_VALUE}
                                    </Link>
                                ) : (
                                    row.customerCode || DEFAULT_VALUE
                                )
                        },
                        {
                            key: "userName",
                            label: "手机号/个人实名",
                            prop: "userName",
                            render: ({ row }) => row.userName || row.mobile || DEFAULT_VALUE
                        },
                        {
                            key: "clientType",
                            label: "终端",
                            prop: "clientType",
                            render: ({ row }) => CLIENT_TYPE.getIntroByCode(row.clientType) || DEFAULT_VALUE
                        },
                        {
                            key: "clickTime",
                            label: "点击时间",
                            width: 160,
                            prop: "clickTime",
                            render: ({ row }) => dateFormat(row.clickTime, DATETIME_PICKER_FORMAT)
                        }
                    ]}
                    pagination={false}
                    rowKey={row => String(row.id || `${row.customerId}-${row.clickTime}`)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <div>
                                {clickDetailRow?.productType
                                    ? `${clickDetailRow.productType.id || DEFAULT_VALUE}/${clickDetailRow.productType.name || DEFAULT_VALUE}`
                                    : DEFAULT_VALUE}
                            </div>

                            {clickDetailError ? <Alert role="alert" message={clickDetailError} type="error" /> : null}

                            {clickDetailLoading ? <Typography.Text type="secondary">加载中</Typography.Text> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={clickDetailPage}
                                pageSize={20}
                                total={clickDetailTotal}
                                hideOnSinglePage
                                onChange={page => setClickDetailPage(page)}
                            />
                        </div>
                    }
                />
            </ZhDialog>
            <ZhDialog
                open={topDetailRow !== null}
                title="置顶明细"
                width={800}
                onCancel={() => setTopDetailRow(null)}
                footer={null}
            >
                <ZhTable<ProductTypeTopVo>
                    data={topDetailRows}
                    columns={[
                        {
                            key: "customerCode",
                            label: "用户ID",
                            prop: "productTypeTop.customerCode",
                            render: ({ row }) => {
                                // 旧接口实际返回 customerCode，类型定义历史上未收录时回退到客户 ID。
                                const top = row.productTypeTop as ProductTypeTopVo["productTypeTop"] & {
                                    customerCode?: string | number;
                                };
                                return top.customerId ? (
                                    <Link to={`/lianYunUser/userInfo?userId=${top.customerId}`}>
                                        {top.customerCode || top.customerId}
                                    </Link>
                                ) : (
                                    DEFAULT_VALUE
                                );
                            }
                        },
                        {
                            key: "userName",
                            label: "手机号/个人实名",
                            prop: "productTypeTop.userName",
                            render: ({ row }) =>
                                row.productTypeTop.userName || row.productTypeTop.mobile || DEFAULT_VALUE
                        },
                        {
                            key: "platform",
                            label: "终端",
                            prop: "platform",
                            render: ({ row }) => row.platform || DEFAULT_VALUE
                        },
                        {
                            key: "operation",
                            label: "操作",
                            prop: "productTypeTop.operation",
                            render: ({ row }) => PIN_TO_TP.getIntroByCode(row.productTypeTop.operation) || DEFAULT_VALUE
                        },
                        {
                            key: "topTime",
                            label: "置顶时间",
                            width: 160,
                            prop: "productTypeTop.topTime",
                            render: ({ row }) => dateFormat(row.productTypeTop.topTime, DATETIME_PICKER_FORMAT)
                        }
                    ]}
                    pagination={false}
                    rowKey={row =>
                        String(
                            row.productTypeTop.id || `${row.productTypeTop.customerId}-${row.productTypeTop.topTime}`
                        )
                    }
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <div>
                                {topDetailRow?.productType
                                    ? `${topDetailRow.productType.id || DEFAULT_VALUE}/${topDetailRow.productType.name || DEFAULT_VALUE}`
                                    : DEFAULT_VALUE}
                            </div>

                            {topDetailError ? <Alert role="alert" message={topDetailError} type="error" /> : null}

                            {topDetailLoading ? <Typography.Text type="secondary">加载中</Typography.Text> : null}
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={topDetailPage}
                                pageSize={20}
                                total={topDetailTotal}
                                hideOnSinglePage
                                onChange={page => setTopDetailPage(page)}
                            />
                        </div>
                    }
                />
            </ZhDialog>
        </section>
    );
}
